import { supabase } from "@/integrations/supabase/client";
import { PUBLIC_SITE_URL } from "@/lib/share";
import type { Purpose } from "@/lib/mock-data";

/* ------------------------------------------------------------------ *
 * Public Partner Profile — read model for the guest-facing page.
 * Only data covered by public (anon) RLS policies is read here:
 *   partner_profiles (status = 'approved')
 *   *_listings       (status = 'published')
 *   listing_reviews  (is_hidden = false)
 * Contact details are NEVER read or exposed on this page.
 * ------------------------------------------------------------------ */

export const PARTNER_SERVICES: Purpose[] = ["sale", "rent", "lease", "parking"];

export const SERVICE_LABEL: Record<Purpose, string> = {
  sale: "Sale",
  rent: "Rent",
  lease: "Lease",
  parking: "Parking",
};

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=70";

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function slugify(value: string) {
  return (
    (value || "partner")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "partner"
  );
}

/** SEO-friendly slug: business-name-<uuid>. */
export function partnerSlug(businessName: string | null, userId: string) {
  return `${slugify(businessName ?? "partner")}-${userId}`;
}

export function partnerIdFromSlug(slug: string): string | null {
  const m = slug.match(UUID_RE);
  return m ? m[0].toLowerCase() : null;
}

export function partnerProfilePath(businessName: string | null, userId: string) {
  return `/p/${partnerSlug(businessName, userId)}`;
}

export function partnerProfileUrl(businessName: string | null, userId: string) {
  return `${PUBLIC_SITE_URL}${partnerProfilePath(businessName, userId)}`;
}

export interface PublicPartnerListing {
  id: string;
  service: Purpose;
  title: string;
  priceLabel: string;
  propertyType: string | null;
  city: string | null;
  address: string | null;
  image: string;
  areaSqft: number | null;
  createdAt: string | null;
}

export interface PublicPartnerProfile {
  userId: string;
  businessName: string;
  description: string | null;
  city: string | null;
  logo: string | null;
  verified: boolean;
  slug: string;
  listings: Record<Purpose, PublicPartnerListing[]>;
  activeListings: number;
  averageRating: number;
  totalReviews: number;
}

function inr(n: number) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

type Row = Record<string, unknown>;

function str(r: Row, k: string) {
  return typeof r[k] === "string" && r[k] ? (r[k] as string) : null;
}

function firstImage(r: Row) {
  const single = str(r, "image_url");
  if (single) return single;
  const list = Array.isArray(r.images) ? (r.images as unknown[]) : [];
  const found = list.find((i): i is string => typeof i === "string" && !!i);
  return found ?? FALLBACK_IMAGE;
}

function priceFor(service: Purpose, r: Row): string {
  const n = (k: string) => (r[k] == null ? 0 : Number(r[k]));
  if (service === "sale") return n("price") ? inr(n("price")) : "Price on request";
  if (service === "rent")
    return n("rent_monthly") ? `${inr(n("rent_monthly"))} / mo` : "Rent on request";
  if (service === "lease")
    return n("lease_amount") ? `${inr(n("lease_amount"))} / mo` : "Lease on request";
  if (n("price_hourly")) return `${inr(n("price_hourly"))} / hr`;
  if (n("price_daily")) return `${inr(n("price_daily"))} / day`;
  if (n("price_monthly")) return `${inr(n("price_monthly"))} / mo`;
  return "Price on request";
}

const TABLE: Record<
  Purpose,
  "sale_listings" | "rent_listings" | "lease_listings" | "parking_listings"
> = {
  sale: "sale_listings",
  rent: "rent_listings",
  lease: "lease_listings",
  parking: "parking_listings",
};

const COLUMNS: Record<Purpose, string> = {
  sale: "id,title,listing_type,city,address,image_url,images,area_sqft,created_at,price",
  rent: "id,title,listing_type,city,address,image_url,images,area_sqft,created_at,rent_monthly",
  lease: "id,title,listing_type,city,address,image_url,images,area_sqft,created_at,lease_amount",
  parking:
    "id,title,parking_type,city,address,image_url,images,created_at,price_hourly,price_daily,price_monthly",
};

async function listingsFor(service: Purpose, ownerId: string): Promise<PublicPartnerListing[]> {
  const { data, error } = await supabase
    .from(TABLE[service])
    .select(COLUMNS[service])
    .eq("owner_id", ownerId)
    .eq("status", "published")
    .order("created_at", { ascending: false })
    .limit(60);
  if (error || !data) return [];
  return (data as unknown as Row[]).map((r) => ({
    id: String(r.id),
    service,
    title: str(r, "title") ?? "Listing",
    priceLabel: priceFor(service, r),
    propertyType: str(r, "listing_type") ?? str(r, "parking_type"),
    city: str(r, "city"),
    address: str(r, "address"),
    image: firstImage(r),
    areaSqft: r.area_sqft == null ? null : Number(r.area_sqft),
    createdAt: str(r, "created_at"),
  }));
}

export async function fetchPublicPartner(slug: string): Promise<PublicPartnerProfile | null> {
  const userId = partnerIdFromSlug(slug);
  if (!userId) return null;

  const { data: profile } = await supabase
    .from("partner_profiles")
    .select("user_id,business_name,description,city,logo,status")
    .eq("user_id", userId)
    .eq("status", "approved")
    .maybeSingle();

  if (!profile) return null;
  const p = profile as unknown as Row;
  const businessName = str(p, "business_name") ?? "Landoo Partner";

  const [sale, rent, lease, parking, reviews] = await Promise.all([
    listingsFor("sale", userId),
    listingsFor("rent", userId),
    listingsFor("lease", userId),
    listingsFor("parking", userId),
    supabase
      .from("listing_reviews")
      .select("rating")
      .eq("partner_id", userId)
      .eq("is_hidden", false)
      .limit(1000),
  ]);

  const ratings = ((reviews.data ?? []) as unknown as Row[]).map((r) => Number(r.rating) || 0);
  const total = ratings.length;
  const average = total ? Number((ratings.reduce((a, b) => a + b, 0) / total).toFixed(1)) : 0;

  const listings = { sale, rent, lease, parking };

  return {
    userId,
    businessName,
    description: str(p, "description"),
    city: str(p, "city"),
    logo: str(p, "logo"),
    verified: str(p, "status") === "approved",
    slug: partnerSlug(businessName, userId),
    listings,
    activeListings: sale.length + rent.length + lease.length + parking.length,
    averageRating: average,
    totalReviews: total,
  };
}

export function partnerSeoTitle(name: string, city: string | null) {
  const base = city ? `${name} — Property Partner in ${city}` : `${name} — Property Partner`;
  const full = `${base} | Landoo`;
  return full.length > 65 ? `${name} | Landoo Partner` : full;
}

export function partnerSeoDescription(p: {
  businessName: string;
  city: string | null;
  activeListings: number;
  averageRating: number;
  totalReviews: number;
  description: string | null;
}) {
  const lead = `${p.businessName}${p.city ? ` in ${p.city}` : ""} — ${p.activeListings} active listing${
    p.activeListings === 1 ? "" : "s"
  } for sale, rent, lease and parking on Landoo${
    p.totalReviews
      ? `. Rated ${p.averageRating}/5 from ${p.totalReviews} review${p.totalReviews === 1 ? "" : "s"}`
      : ""
  }.`;
  const tail = (p.description ?? "").replace(/\s+/g, " ").trim();
  const text = [lead, tail].filter(Boolean).join(" ");
  return text.length > 155 ? `${text.slice(0, 152)}…` : text;
}
